/**
 * Homly — Design Kit Page (interne)
 * =================================
 * Aperçu visuel de tous les composants atomiques du nouveau design system "Dark Luxury".
 * Sert de référence visuelle pour la refonte des dashboards.
 *
 * Route : /design-kit (accès libre, page interne pour validation)
 */
import React from 'react';
import {
    Phone, MessageSquare, FileSignature, Calendar, Sparkles, TrendingUp,
    Users, Home, AlertTriangle, Eye, Send, ArrowRight, CheckCircle2,
} from 'lucide-react';

import {
    ActionCard, AIRecommendation, EmptyState,
    StatusBadge, PriorityBadge, KPIWithExplanation,
    PageHeader, SectionBlock, HomlyButton, HelpHint,
} from '../../components/homly';

export default function DesignKitPage() {
    return (
        <div
            data-testid="design-kit-page"
            style={{
                minHeight: '100vh',
                background: 'var(--homly-gradient-page-bg)',
                color: 'var(--homly-text-primary)',
                fontFamily: 'var(--homly-font-body)',
                padding: 'var(--homly-space-8) var(--homly-space-6)',
            }}
        >
            <div style={{ maxWidth: 1180, margin: '0 auto' }}>
                <PageHeader
                    kicker="Design System · Phase 0"
                    title={<span><span className="homly-text-gradient">Homly Dark Luxury</span> — Design Kit</span>}
                    subtitle="Source unique du langage visuel V1. Tous les composants ci-dessous utilisent les tokens CSS de homly-design-tokens.css. Toute refonte des dashboards s'appuie sur ces briques."
                    primaryAction={{ label: "Validé, on continue", icon: CheckCircle2, onClick: () => {} }}
                    secondaryAction={{ label: "Voir les tokens" }}
                    dataTestid="design-kit-header"
                />

                {/* ── BUTTONS ────────────────────────────────────────────── */}
                <SectionBlock
                    kicker="01 · CTA"
                    title="Boutons"
                    helpText="Toujours préférer un seul CTA primaire par bloc. Le secondaire reste discret."
                    dataTestid="dk-buttons"
                >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <HomlyButton variant="primary" icon={Sparkles} dataTestid="dk-btn-primary">Action primaire</HomlyButton>
                        <HomlyButton variant="secondary" icon={Send}>Action orange</HomlyButton>
                        <HomlyButton variant="outline" icon={Eye}>Voir le détail</HomlyButton>
                        <HomlyButton variant="ghost">Plus tard</HomlyButton>
                        <HomlyButton variant="destructive" icon={AlertTriangle}>Résilier</HomlyButton>
                        <HomlyButton variant="primary" size="sm">Petit</HomlyButton>
                        <HomlyButton variant="primary" size="lg" iconRight={ArrowRight}>Réserver une démo</HomlyButton>
                        <HomlyButton variant="primary" disabled>Désactivé</HomlyButton>
                    </div>
                </SectionBlock>

                {/* ── BADGES ─────────────────────────────────────────────── */}
                <SectionBlock
                    kicker="02 · Badges"
                    title="Statuts de mandat & priorités"
                    helpText="Vocabulaire métier français. Plus jamais de jargon technique."
                    dataTestid="dk-badges"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        <div>
                            <p style={subLabel}>Statuts mandat</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                <StatusBadge status="draft" />
                                <StatusBadge status="sent" />
                                <StatusBadge status="signed" />
                                <StatusBadge status="active" />
                                <StatusBadge status="at_risk" />
                                <StatusBadge status="sold" />
                                <StatusBadge status="archived" />
                            </div>
                        </div>
                        <div>
                            <p style={subLabel}>Priorité d'action</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                <PriorityBadge priority="critical" />
                                <PriorityBadge priority="high" />
                                <PriorityBadge priority="medium" />
                                <PriorityBadge priority="low" />
                                <PriorityBadge priority="ok" />
                            </div>
                        </div>
                    </div>
                </SectionBlock>

                {/* ── KPI CARDS ──────────────────────────────────────────── */}
                <SectionBlock
                    kicker="03 · KPI"
                    title="Indicateurs avec interprétation humaine"
                    helpText="Chaque chiffre est accompagné d'une phrase qui explique ce qu'il signifie pour l'utilisateur."
                    dataTestid="dk-kpis"
                >
                    <div style={gridResponsive(3)}>
                        <KPIWithExplanation
                            label="Chiffre d'affaires · ce mois"
                            value="81,9"
                            unit="k€"
                            trend={{ value: 18, isPositiveGood: true }}
                            explanation="Mois solide. Vous êtes à 102 % de votre objectif (80 k€). Maintenez le rythme commercial."
                            icon={TrendingUp}
                            accent="purple"
                            onClick={() => {}}
                        />
                        <KPIWithExplanation
                            label="Taux de chute compromis"
                            value="36"
                            unit="%"
                            trend={{ value: 12, isPositiveGood: false }}
                            explanation="3× le benchmark national (12 %). Concentrez les efforts sur la vérification du financement avant signature."
                            icon={AlertTriangle}
                            accent="red"
                        />
                        <KPIWithExplanation
                            label="Mandats actifs"
                            value="22"
                            unit=""
                            trend={{ value: 0 }}
                            explanation="Bon volume pour 2 agents. 5 sont dormants depuis +30 jours, à réveiller cette semaine."
                            icon={Home}
                            accent="orange"
                        />
                    </div>
                </SectionBlock>

                {/* ── ACTION CARDS ───────────────────────────────────────── */}
                <SectionBlock
                    kicker="04 · Actions"
                    title="Carte d'action prioritaire"
                    helpText={`Le composant central des dashboards refondus : "Que dois-je faire maintenant ?".`}
                    dataTestid="dk-actions"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <ActionCard
                            priority="critical"
                            icon={Phone}
                            title="Appeler Jean Lambert"
                            context="Mandat exclusif Marseille · sans contact depuis 21 jours · risque pré-dénonciation élevé"
                            ctaLabel="Préparer le point"
                            timestamp="il y a 2 h"
                            onCtaClick={() => {}}
                        />
                        <ActionCard
                            priority="high"
                            icon={MessageSquare}
                            title="Répondre à Marie Dupont"
                            context="Question vendeur sur l'estimation reçue ce matin via l'espace vendeur"
                            ctaLabel="Voir la conversation"
                            timestamp="il y a 4 h"
                            onCtaClick={() => {}}
                        />
                        <ActionCard
                            priority="medium"
                            icon={FileSignature}
                            title="Faire signer le mandat de M. Bernard"
                            context="Mandat préparé samedi · en attente de signature depuis 5 jours"
                            ctaLabel="Relancer la signature"
                            onCtaClick={() => {}}
                        />
                        <ActionCard
                            priority="low"
                            icon={Calendar}
                            title="Préparer la visite de samedi"
                            context="Appartement Bellecour · 3 candidats confirmés"
                            ctaLabel="Voir le planning"
                            onCtaClick={() => {}}
                        />
                    </div>
                </SectionBlock>

                {/* ── AI RECOMMENDATIONS ─────────────────────────────────── */}
                <SectionBlock
                    kicker="05 · IA actionnable"
                    title="Recommandations IA contextualisées"
                    helpText={`Phrase humaine + bouton d'action concret. Plus jamais d'IA "qui parle dans le vide".`}
                    rightSlot={<HelpHint>Chaque reco IA s'appuie sur les KPI du contexte de l'utilisateur. Cf. ai_context_builder.py.</HelpHint>}
                    dataTestid="dk-ai"
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <AIRecommendation
                            severity="critical"
                            confidence={92}
                            headline="9 vendeurs sans contact depuis 14 jours — 82 130 € d'honoraires en jeu"
                            explanation="Le silence est la cause #1 de dénonciation de mandat. Lancez une campagne SMS ce matin pour reprendre contact."
                            action={{ label: 'Lancer la campagne SMS', icon: Send, onClick: () => {} }}
                            secondaryAction={{ label: 'Voir le détail' }}
                            dismissable
                        />
                        <AIRecommendation
                            severity="medium"
                            confidence={78}
                            headline="M. Dupont consulte peu son espace vendeur depuis 10 jours"
                            explanation="Proposez-lui un point téléphonique pour renforcer son engagement avant qu'il ne se désintéresse."
                            action={{ label: 'Préparer la relance', icon: Phone, onClick: () => {} }}
                        />
                        <AIRecommendation
                            severity="positive"
                            headline="Emma Rousseau enchaîne 3 ventes sur le mois — au-dessus du Top 20% national"
                            explanation="Profitez-en pour lui demander de partager ses bonnes pratiques en réunion équipe vendredi."
                            action={{ label: 'Envoyer un message', icon: MessageSquare, onClick: () => {} }}
                        />
                    </div>
                </SectionBlock>

                {/* ── EMPTY STATE ────────────────────────────────────────── */}
                <SectionBlock
                    kicker="06 · États vides"
                    title="Empty states explicatifs"
                    helpText={`Plus jamais de "Aucune donnée" froid. On rassure, on explique, on guide.`}
                    dataTestid="dk-empty"
                >
                    <div
                        style={{
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-border)',
                            borderRadius: 'var(--homly-radius-lg)',
                        }}
                    >
                        <EmptyState
                            icon={Users}
                            title="Aucun vendeur connecté pour l'instant"
                            description="Les espaces vendeurs apparaîtront ici dès qu'un de vos vendeurs consultera son lien de suivi. Vous recevrez une notification."
                            primaryAction={{ label: 'Inviter un vendeur', icon: Send, onClick: () => {} }}
                            secondaryAction={{ label: 'Comment ça marche ?' }}
                            accent="purple"
                        />
                    </div>
                </SectionBlock>

                {/* ── COMBO : mini-cockpit ───────────────────────────────── */}
                <SectionBlock
                    kicker="07 · Composition"
                    title="Exemple d'assemblage : mini-cockpit Manager"
                    helpText="Aperçu d'une page dashboard refondue : urgences en haut, KPIs avec interprétation, reco IA prête à l'action."
                    dataTestid="dk-combo"
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 18,
                            padding: 'var(--homly-space-6)',
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-border)',
                            borderRadius: 'var(--homly-radius-xl)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                                <p style={{ ...subLabel, color: 'var(--homly-orange)' }}>Bonjour Sophie, voici votre cockpit du jour</p>
                                <h3 style={{ margin: 0, fontFamily: 'var(--homly-font-display)', fontSize: 20, fontWeight: 700 }}>
                                    3 actions urgentes vous attendent
                                </h3>
                            </div>
                            <PriorityBadge priority="critical" label="2 urgences vendeur" />
                        </div>

                        <AIRecommendation
                            severity="critical"
                            confidence={92}
                            headline="Jean Lambert — risque de pré-dénonciation détecté"
                            explanation="Mandat en silence 21 jours · 3 messages frustrés cette semaine · échéance contractuelle dans 14 jours."
                            action={{ label: 'Préparer mon point', icon: Phone, onClick: () => {} }}
                            secondaryAction={{ label: 'Voir le mandat' }}
                        />

                        <div style={gridResponsive(3)}>
                            <KPIWithExplanation
                                label="CA · ce mois"
                                value="81,9"
                                unit="k€"
                                trend={{ value: 18, isPositiveGood: true }}
                                explanation="102 % de votre objectif mensuel."
                                icon={TrendingUp}
                                accent="purple"
                                size="sm"
                            />
                            <KPIWithExplanation
                                label="Mandats à risque"
                                value="3"
                                trend={{ value: 50, isPositiveGood: false }}
                                explanation="Dont 1 en zone rouge urgente."
                                icon={AlertTriangle}
                                accent="red"
                                size="sm"
                            />
                            <KPIWithExplanation
                                label="HomlyScore agence"
                                value="1 287"
                                trend={{ value: 12, isPositiveGood: true }}
                                explanation="Niveau Or · 42 avis confirmés."
                                icon={Sparkles}
                                accent="orange"
                                size="sm"
                            />
                        </div>
                    </div>
                </SectionBlock>

                {/* Footer */}
                <footer
                    style={{
                        marginTop: 64,
                        paddingTop: 24,
                        borderTop: '1px solid var(--homly-border-subtle)',
                        fontSize: 12,
                        color: 'var(--homly-text-tertiary)',
                        fontFamily: 'var(--homly-font-mono)',
                        textAlign: 'center',
                    }}
                >
                    Homly Design System · Dark Luxury · Phase 0 — Build {new Date().toISOString().slice(0, 10)}
                </footer>
            </div>
        </div>
    );
}

// ─── styles utilitaires inline ───
const subLabel = {
    margin: '0 0 8px 0',
    fontFamily: 'var(--homly-font-display)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'var(--homly-text-tertiary)',
};

function gridResponsive(cols = 3) {
    return {
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))`,
        gap: 14,
    };
}
